import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Text } from 'rizzui';
import { Loader } from '../../../components/loader/SpinnerLogo';
import useFetch from '../../../hooks/useFetch';
import { menuRoutes } from '../../../router/menu';
import { FacilitatorCallsService } from '../../../services/calls/facilitatorCallsService';

const panelClassName = 'rounded-md border border-gray-200 bg-white p-4';

const stageLabels: Record<string, string> = {
    start: 'Inicio',
    follow_up: 'Seguimiento',
    end: 'Fin',
    completed: 'Completado',
};

const formatDateValue = (value: any): string => {
    if (!value) return 'Sin contacto';
    if (typeof value === 'string') return value;
    if (typeof value?.date === 'string') return value.date;
    return 'Sin contacto';
};

const FacilitatorDashboard = () => {
    const service = new FacilitatorCallsService();

    const [response, loading, error] = useFetch(useCallback(async () => {
        const apiResponse = await service.getDashboardAssignments();
        return apiResponse.getResponseData();
    }, []));

    const summary = response?.summary ?? {
        assignedOrders: 0,
        assignedContracts: 0,
    };
    const orders = response?.orders ?? [];

    if (loading && !response) {
        return <Loader height="50vh" />;
    }

    if (!loading && error) {
        return (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                No se ha podido cargar el dashboard de dinamización.
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
                {[
                    { label: 'Pedidos asignados', value: summary.assignedOrders },
                    { label: 'Contratos asignados', value: summary.assignedContracts },
                ].map((card) => (
                    <div key={card.label} className={panelClassName}>
                        <Text className="text-xs uppercase text-gray-500">{card.label}</Text>
                        <Text className="mt-2 text-2xl font-semibold text-gray-900">{card.value}</Text>
                    </div>
                ))}
            </div>

            <div className={panelClassName}>
                <div className="flex items-center justify-between gap-3">
                    <Text className="text-base font-semibold text-gray-900">Pedidos asignados</Text>
                </div>

                <div className="mt-4 space-y-4">
                    {orders.length === 0 && (
                        <Text className="text-sm text-gray-500">No tienes pedidos asignados en campañas activas.</Text>
                    )}

                    {orders.map((order: any) => (
                        <div key={order.orderId} className="rounded-md border border-gray-200">
                            <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
                                <div>
                                    <Link
                                        className="text-sm font-semibold text-primary"
                                        to={`${menuRoutes.orders.path}/${order.orderId}/profile${menuRoutes.orders.profile.campaigns}`}
                                    >
                                        {order.orderCode || 'Pedido'}
                                    </Link>
                                    <Text className="mt-1 text-xs text-gray-500">
                                        {order.contractsCount} contrato{order.contractsCount === 1 ? '' : 's'} asignado{order.contractsCount === 1 ? '' : 's'}
                                    </Text>
                                </div>
                            </div>

                            <div className="divide-y divide-gray-200">
                                {order.contracts.map((contract: any) => (
                                    <div key={contract.contractId} className="flex items-start justify-between gap-4 px-4 py-3">
                                        <div className="min-w-0">
                                            <Link
                                                className="text-sm font-semibold text-primary"
                                                to={`${menuRoutes.contracts.path}/${contract.contractId}/profile${menuRoutes.contracts.profile.tracking}`}
                                            >
                                                {contract.contractCode || 'Contrato'}
                                            </Link>
                                            <Text className="mt-1 text-sm text-gray-700">{contract.studentFullName || 'Sin alumno'}</Text>
                                            <Text className="mt-1 text-xs text-gray-500">{contract.productLabel || 'Sin acción formativa'}</Text>
                                        </div>
                                        <div className="shrink-0 text-right">
                                            <Text className="text-xs font-medium text-gray-700">
                                                {stageLabels[contract.currentStage] || contract.currentStage || 'N/A'}
                                            </Text>
                                            <Text className="mt-1 text-xs text-gray-500">{formatDateValue(contract.lastContactAt)}</Text>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default FacilitatorDashboard;
